import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	FormGroupComponent,
	InfoGroupComponent,
	ISelect,
	ITagImage,
	ModalComponent,
	SelectComponent,
	TileComponent,
	ToggleComponent,
} from '@tecalisdev/infinity';
import { UserRole } from '../../../../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { ReactiveFormsService } from '../../../../../shared/services/reactive-forms.service';
import { SnackbarService } from '../../../../../shared/services/snackbar.service';
import { IUsersListBody, IUserUpdatePayload } from '../../models/users.models';
import { UsersService } from '../../services/users.service';

@Component({
	selector: 'app-users-form',
	imports: [
		TranslatePipe,
		ReactiveFormsModule,
		ModalComponent,
		AlertComponent,
		FormGroupComponent,
		ToggleComponent,
		TileComponent,
		InfoGroupComponent,
		SelectComponent,
	],
	templateUrl: './users-form.component.html',
	styleUrl: './users-form.component.scss',
})
export class UsersFormComponent implements OnInit {
	@Input() detail: IUsersListBody;
	@Output() closeEvent = new EventEmitter<boolean>();

	private usersService = inject(UsersService);
	private destroyRef = inject(DestroyRef);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);
	private rolePermissionsService = inject(RolePermissionsService);
	public masterService = inject(MasterService);
	public reactiveFormService = inject(ReactiveFormsService);

	/** Solo superadmin puede reasignar compañías. */
	public readonly canEditCompanies: boolean = this.rolePermissionsService.isSuperadmin();
	public readonly modalTagImage: ITagImage = { icon: 'icon-user' };
	public readonly isSaving = signal(false);
	public roles: Array<ISelect> = this.buildAssignableRoles();
	public errorMsg: string = '';
	public isSupervisorRoleSelected: boolean = false;
	public form: FormGroup = new FormGroup({
		companies_uuid: new FormControl([], Validators.required),
		role_uuid: new FormControl(this.roles.length > 0 ? this.roles[0] : '', Validators.required),
	});

	get primaryButtonLabel(): string {
		if (this.isSaving()) {
			return this.trans.instant('BUTTON.SAVING');
		}
		return this.trans.instant('BUTTON.SAVE');
	}

	ngOnInit(): void {
		if (this.detail) {
			this.form.patchValue({
				companies_uuid: this.getCompaniesUuidsFromDetail(this.detail),
				role_uuid: this.masterService.getUuidRoleByName(this.detail.role),
			});
		}
		if (!this.canEditCompanies) {
			this.form.get('companies_uuid')?.disable({ emitEvent: false });
		}
		this.watchRoleChanges();
		this.syncCompaniesControlWithRole();
	}

	public closeModal(type: string) {
		if (this.isSaving()) {
			return;
		}
		const actions = {
			buttonPrimary: () => {
				if (this.form.invalid) {
					this.form.markAllAsTouched();
					return;
				}
				this.updateRole();
			},
		};

		actions[type] ? actions[type]() : this.closeEvent.emit(false);
	}

	private updateRole(): void {
		this.isSaving.set(true);
		this.usersService.update(this.detail.uuid, this.getPayload()).subscribe({
			next: () => {
				this.closeEvent.emit(true);
				this.snackbarService.show(this.trans.instant('USERS.LIST.UPDATE_CONFIRM'));
			},
			error: (error: HttpErrorResponse) => {
				this.errorMsg = this.getUpdateErrorMessage(error);
				this.isSaving.set(false);
			},
		});
	}

	private getPayload(): IUserUpdatePayload {
		if (!this.canEditCompanies) {
			return { role_uuid: this.getRoleUuid() };
		}

		return {
			companies_uuid: this.normalizeCompaniesValue(this.form.get('companies_uuid')?.value),
			role_uuid: this.getRoleUuid(),
		};
	}

	public getRoleDescription(): string {
		const roleUuid = this.getRoleUuid();
		const role = this.masterService.master()?.roles?.find((r) => r.uuid === roleUuid);
		if (!role?.name) return '';

		const descriptionKey = `MASTER.ROLES.DESCRIPTION.${role.name.toUpperCase()}`;
		return this.trans.instant(descriptionKey);
	}

	private watchRoleChanges(): void {
		this.form
			.get('role_uuid')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.syncCompaniesControlWithRole());
	}

	private syncCompaniesControlWithRole(): void {
		if (!this.canEditCompanies) {
			this.isSupervisorRoleSelected = false;
			return;
		}

		const isSupervisorRoleSelected = this.rolePermissionsService.isSupervisor(this.getRoleUuid());
		const companiesControl = this.form.get('companies_uuid');
		const normalizedCompanies = this.normalizeCompaniesValue(companiesControl?.value);

		if (isSupervisorRoleSelected) {
			// Asegura array antes de activar multiselección para evitar `push` sobre string.
			companiesControl?.setValue(normalizedCompanies, { emitEvent: false });
			this.isSupervisorRoleSelected = true;
			return;
		}

		this.isSupervisorRoleSelected = false;
		// En modo single el select necesita un escalar para mostrar correctamente la opción elegida.
		companiesControl?.setValue(normalizedCompanies[0] ?? '', { emitEvent: false });
	}

	/** No-superadmin: excluye SUPERADMIN y SUPERVISOR (NIF-749). */
	private buildAssignableRoles(): Array<ISelect> {
		const allRoles = this.masterService.roles ?? [];
		if (this.rolePermissionsService.isSuperadmin()) {
			return allRoles;
		}

		const blockedRoles = new Set<string>([UserRole.SUPERADMIN, UserRole.SUPERVISOR]);
		const masterRoles = this.masterService.master()?.roles ?? [];
		const allowedUuids = new Set(
			masterRoles
				.filter((role) => !blockedRoles.has((role.name ?? '').toLowerCase()))
				.map((role) => String(role.uuid)),
		);

		return allRoles.filter((option) => allowedUuids.has(String(option.id)));
	}

	private normalizeCompaniesValue(value: unknown): string[] {
		if (Array.isArray(value)) {
			return value.map((company) => this.extractSelectId(company)).filter(Boolean);
		}

		const companyId = this.extractSelectId(value);
		return companyId ? [companyId] : [];
	}

	private extractSelectId(value: unknown): string {
		if (!value) return '';
		if (typeof value === 'string') return value;
		if (typeof value === 'object' && 'id' in value) {
			return String((value as ISelect).id ?? '');
		}
		return '';
	}

	private getRoleUuid(): string {
		const roleValue = this.form.get('role_uuid')?.value;
		return this.extractSelectId(roleValue);
	}

	private getUpdateErrorMessage(error: HttpErrorResponse): string {
		const errorKeyByStatus: Record<number, string> = {
			400: 'USERS.LIST.UPDATE_ERROR_VALIDATION',
			401: 'USERS.LIST.UPDATE_ERROR_UNAUTHORIZED',
			403: 'USERS.LIST.UPDATE_ERROR_UNAUTHORIZED',
			422: 'USERS.LIST.UPDATE_ERROR_VALIDATION',
		};

		const errorKey = errorKeyByStatus[error?.status] || 'USERS.LIST.UPDATE_ERROR';
		return this.trans.instant(errorKey);
	}

	private getCompaniesUuidsFromDetail(detail: IUsersListBody): string[] {
		return Array.isArray(detail?.companies_uuid) ? detail.companies_uuid.filter(Boolean) : [];
	}
}
